import type { IconName } from '@zapier/design-system';

//////////////////////////////////////////////////////////////////////
// Link typings
//////////////////////////////////////////////////////////////////////

export type UniversalNavLinkType = {
  href: string | null;
  icon?: IconName;
  isExternal?: boolean;
  text: string;
  description?: string;
  submenu?: Array<UniversalNavLinkType>;
  target?: string;
};

//////////////////////////////////////////////////////////////////////
// Topbar links
//////////////////////////////////////////////////////////////////////

export const whyZapierLink: UniversalNavLinkType = {
  href: null,
  text: 'Product',
  submenu: [
    {
      href: '/how-it-works',
      text: 'How it works',
    },
    {
      href: '/features',
      text: 'Features',
    },
    {
      href: '/customers',
      text: 'Customer stories',
    },
    {
      href: '/security',
      text: 'Security',
    },
  ],
};

export const exploreLink: UniversalNavLinkType = {
  href: null,
  text: 'Explore',
  submenu: [
    {
      href: '/explore',
      text: 'Popular ways to use Zapier',
    },
    {
      href: '/apps',
      text: 'Apps that work with Zapier',
    },
    {
      href: '/roles',
      text: 'Explore Zapier by job role',
    },
  ],
};

export const resourcesLink: UniversalNavLinkType = {
  href: null,
  text: 'Resources',
  submenu: [
    {
      href: '/blog/',
      text: 'Blog',
      description:
        'Read the Zapier blog for tips on productivity, automation, and growing your business',
    },
    {
      href: '/experts',
      text: 'Experts',
      description:
        'Hire a Zapier Expert to help you improve processes and automate workflows.',
    },
    {
      href: '/help',
      text: 'Help Center',
      description:
        'Get help with Zapier from our tutorials, FAQs, and troubleshooting articles.',
    },
    {
      href: 'https://community.zapier.com',
      text: 'Community',
      description:
        'Ask questions, share your knowledge, and get inspired by other Zapier users.',
    },
    {
      href: '/university',
      text: 'Zapier University',
      description:
        'Video courses designed to help you become a better Zapier user.',
    },
    {
      href: 'https://go.zapier.com/webinars-on-demand/',
      text: 'Webinars',
      description:
        'Learn about automation anytime, anywhere with our on-demand webinar library.',
    },
  ],
};

export const teamsLink: UniversalNavLinkType = {
  href: '/teams-companies',
  text: 'Teams & Companies',
};

export const pricingLink: UniversalNavLinkType = {
  href: '/pricing',
  text: 'Pricing',
};

//////////////////////////////////////////////////////////////////////
// Sidebar links
//////////////////////////////////////////////////////////////////////

export const dashboardLink: UniversalNavLinkType = {
  href: '/app/dashboard',
  icon: 'navDashboard' as 'navDashboard',
  text: 'Dashboard',
};

export const zapsLink: UniversalNavLinkType = {
  href: '/app/zaps',
  icon: 'miscBoltAlt' as 'miscBoltAlt',
  text: 'Zaps',
};

export const myAppsLink: UniversalNavLinkType = {
  href: '/app/connections',
  icon: 'navApps' as 'navApps',
  text: 'My Apps',
};

export const historyLink: UniversalNavLinkType = {
  href: '/app/history/usage',
  icon: 'miscClock' as 'miscClock',
  text: 'Zap History',
};

export const exploreLinkInternal: UniversalNavLinkType = {
  href: '/explore',
  icon: 'navExplore' as 'navExplore',
  text: 'Explore',
};

export const helpLink: UniversalNavLinkType = {
  href: '/help',
  icon: 'navHelp' as 'navHelp',
  isExternal: true,
  text: 'Get Help',
};

export const usageLink: UniversalNavLinkType = {
  href: '/app/settings/billing',
  icon: 'miscWallet' as 'miscWallet',
  text: 'Usage',
};

export const transferLink: UniversalNavLinkType = {
  href: '/app/transfers',
  icon: 'arrowConnect' as 'arrowConnect',
  text: 'Transfers',
};
//////////////////////////////////////////////////////////////////////
// Account menu links
//////////////////////////////////////////////////////////////////////

export const moveZapsLink: UniversalNavLinkType = {
  href: '/app/move-zaps/',
  icon: 'arrowBigRight' as 'arrowBigRight',
  text: 'Move Zaps',
};

export const settingsLink: UniversalNavLinkType = {
  href: '/app/settings/profile',
  icon: 'navCog' as 'navCog',
  text: 'Settings',
};

export const logOutLink: UniversalNavLinkType = {
  href: '/logout',
  icon: 'arrowExit' as 'arrowExit',
  text: 'Log Out',
};

//////////////////////////////////////////////////////////////////////
// Groups of links
//////////////////////////////////////////////////////////////////////

export const UniversalSidebarLinks = (
  sideNavData: Array<UniversalNavLinkType>
): Array<UniversalNavLinkType> => [
  dashboardLink,
  zapsLink,
  transferLink,
  ...sideNavData,
  myAppsLink,
  historyLink,
  exploreLinkInternal,
  helpLink,
];

export const UniversalTopbarLinks: Array<UniversalNavLinkType> = [
  whyZapierLink,
  exploreLink,
  resourcesLink,
  teamsLink,
  pricingLink,
];

export const AccountsMenuLinks: Array<UniversalNavLinkType> = [
  // `moveZapsLink` conditionally rendered.
  settingsLink,
  logOutLink,
];
